/**
 * <s-welcome-popup> — opens the first-order welcome dialog once per visitor.
 *
 * Attributes:
 *   delay          — seconds to wait before opening (default 6)
 *   data-customer  — present when a customer is logged in; never auto-opens
 *
 * Remembers dismissal in localStorage so returning visitors are not interrupted.
 * Handles the dialog's --copy-code command to copy the revealed discount code.
 */
const STORAGE_KEY = 'ginzo-welcome-popup-seen';

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    dialog.addEventListener('close', () => this.#remember(), opts);
    dialog.addEventListener('command', (e) => {
      if (e.command !== '--copy-code') return;
      e.preventDefault();
      this.#copy(e.source);
    }, opts);

    if (dialog.open || this.hasAttribute('data-customer') || this.#seen()) return;

    const delay = Math.max(0, Number(this.getAttribute('delay')) || 0) * 1000;
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog:modal')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #seen() {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  }

  #remember() {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* storage unavailable */ }
  }

  async #copy(source) {
    const code = this.querySelector('[data-welcome-code]')?.textContent?.trim();
    if (!code || !(source instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
      const label = source.dataset.copiedLabel;
      if (label) source.textContent = label;
    } catch { /* clipboard unavailable; code stays visible to copy manually */ }
  }
}

customElements.define('s-welcome-popup', SWelcomePopup);
